/**
 * inline-presentation-assets — browser-runnable version.
 *
 * Mirrors `apps/api/scripts/inline-presentation-assets.mjs` so the
 * "drop a deck on this zone, we'll inline it for you" path in the
 * SPA editor uses the same pipeline as the CLI:
 *
 *   1. Fetch every external <img>/<source>/<link rel=stylesheet>/
 *      <script src> + recursive CSS @import + url(...) refs.
 *   2. Base64-inline binaries as data: URIs, fold stylesheets/scripts
 *      into inline blocks.
 *   3. Replace Font Awesome <i class="fas fa-X"> tags with inline SVG
 *      from cdn.jsdelivr.net (sized to 1em, currentColor fill).
 *   4. Strip non-stylesheet <link> tags (preconnect, dns-prefetch,
 *      icon, etc.) — they only matter for external loads.
 *   5. Report stats + warnings (forbidden tags, oversize). Does NOT
 *      remove forbidden tags — caller decides.
 *
 * Differs from the Node version: no filesystem-relative path inlining.
 * Drop-zone hands us a single File; that file's relative refs can't
 * be resolved without the sibling files, which the browser doesn't see.
 * The CLI is the right tool when the deck has sibling assets.
 */

const VALIDATOR_MAX_BYTES = 5 * 1024 * 1024;
const FORBIDDEN_TAG_RE = /<\s*(iframe|object|embed|frameset|frame)\b/gi;

const MIME_BY_EXT: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
  '.ico': 'image/x-icon',
  '.woff': 'font/woff',
  '.woff2': 'font/woff2',
  '.ttf': 'font/ttf',
  '.otf': 'font/otf',
  '.css': 'text/css',
  '.js': 'application/javascript',
};

const FA_FAMILY_TOKENS = new Set([
  'fa',
  'fas',
  'far',
  'fab',
  'fal',
  'fad',
  'fa-solid',
  'fa-regular',
  'fa-brands',
  'fa-light',
  'fa-duotone',
]);
const FA_SIZE_TOKENS = new Set([
  'fa-fw',
  'fa-lg',
  'fa-xs',
  'fa-sm',
  'fa-2x',
  'fa-3x',
  'fa-4x',
  'fa-5x',
  'fa-spin',
  'fa-pulse',
  'fa-border',
  'fa-pull-left',
  'fa-pull-right',
]);

export interface InlinerStats {
  inlinedAssets: number;
  inlinedCss: number;
  inlinedJs: number;
  strippedLinks: number;
  faIconsSwapped: number;
  errors: string[];
  forbiddenTags: { tag: string; count: number }[];
  inputBytes: number;
  outputBytes: number;
  overSize: boolean;
}

export interface InlinerOptions {
  /** If false, skip the FA swap entirely. Default true. */
  swapFontAwesome?: boolean;
  /** Optional progress callback — fires after each fetch resolves. */
  onProgress?: (msg: string) => void;
}

export async function inlinePresentationHtml(
  original: string,
  options: InlinerOptions = {},
): Promise<{ html: string; stats: InlinerStats }> {
  const { swapFontAwesome = true, onProgress } = options;
  const stats: InlinerStats = {
    inlinedAssets: 0,
    inlinedCss: 0,
    inlinedJs: 0,
    strippedLinks: 0,
    faIconsSwapped: 0,
    errors: [],
    forbiddenTags: [],
    inputBytes: byteLength(original),
    outputBytes: 0,
    overSize: false,
  };

  let html = original;
  if (swapFontAwesome) html = await swapFaIcons(html, stats, onProgress);
  html = await inlineHtmlExternals(html, stats, onProgress);

  // Detect remaining forbidden tags (don't remove — let the author decide).
  const counts = new Map<string, number>();
  FORBIDDEN_TAG_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = FORBIDDEN_TAG_RE.exec(html)) !== null) {
    const t = m[1]!.toLowerCase();
    counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  stats.forbiddenTags = [...counts.entries()].map(([tag, count]) => ({ tag, count }));

  stats.outputBytes = byteLength(html);
  stats.overSize = stats.outputBytes > VALIDATOR_MAX_BYTES;

  return { html, stats };
}

// ─── Font Awesome icon swap ──────────────────────────────────────────

async function swapFaIcons(
  html: string,
  stats: InlinerStats,
  onProgress?: (msg: string) => void,
): Promise<string> {
  const names = new Set<string>();
  const classRe = /class\s*=\s*["']([^"']*)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = classRe.exec(html)) !== null) {
    const tokens = m[1]!.split(/\s+/);
    if (!tokens.some((t) => FA_FAMILY_TOKENS.has(t))) continue;
    for (const cls of tokens) {
      if (cls.startsWith('fa-') && !FA_FAMILY_TOKENS.has(cls) && !FA_SIZE_TOKENS.has(cls)) {
        names.add(cls.slice(3));
      }
    }
  }
  if (names.size === 0) return html;

  onProgress?.(`Fetching ${names.size} Font Awesome icon SVG(s)…`);
  const svgMap = new Map<string, string>();
  await Promise.all(
    [...names].map(async (name) => {
      const url = `https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.5.1/svgs/solid/${name}.svg`;
      try {
        const res = await fetch(url);
        if (!res.ok) {
          stats.errors.push(`fa:${name}: HTTP ${res.status}`);
          return;
        }
        let svg = await res.text();
        svg = svg.replace(/<!--[\s\S]*?-->/g, '');
        svg = svg.replace(/<svg\b([^>]*)>/, (_, attrs: string) => {
          const a = attrs.replace(/\sxmlns="[^"]*"/g, '');
          return `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor"${a} class="fa-svg fa-svg-${name}" style="width:1em;height:1em;vertical-align:-0.125em">`;
        });
        svgMap.set(name, svg.trim());
      } catch (err) {
        stats.errors.push(`fa:${name}: ${(err as Error).message}`);
      }
    }),
  );

  // Replace <i class="...fa-solid|fas fa-X..."></i> blocks
  html = html.replace(
    /<i\b([^>]*?)\bclass\s*=\s*["']([^"']*)["']([^>]*?)>\s*<\/i>/gi,
    (full, _pre, classes: string, _post) => {
      const tokens = classes.split(/\s+/);
      if (!tokens.some((t) => FA_FAMILY_TOKENS.has(t))) return full;
      const iconClass = tokens.find(
        (c) => c.startsWith('fa-') && !FA_FAMILY_TOKENS.has(c) && !FA_SIZE_TOKENS.has(c),
      );
      if (!iconClass) return full;
      const name = iconClass.slice(3);
      const svg = svgMap.get(name);
      if (!svg) return full;
      stats.faIconsSwapped++;
      const otherClasses = tokens
        .filter((c) => !c.startsWith('fa-') && !FA_FAMILY_TOKENS.has(c) && !FA_SIZE_TOKENS.has(c))
        .join(' ');
      return otherClasses ? `<span class="${otherClasses}">${svg}</span>` : svg;
    },
  );

  // Drop the Font Awesome <link rel=stylesheet> — the SVGs are self-contained.
  html = html.replace(/<link\b[^>]*\bhref\s*=\s*["'][^"']*font-?awesome[^"']*["'][^>]*\/?>/gi, '');

  return html;
}

// ─── External resource inliner ───────────────────────────────────────

interface Blob {
  bytes: Uint8Array;
  mime: string;
}
interface Text {
  text: string;
  mime: string;
}

const cache = new Map<string, Blob | Text>();

async function fetchBinary(url: string): Promise<Blob> {
  const cacheKey = `B::${url}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey)! as Blob;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const mime = (res.headers.get('content-type') || guessMime(url)).split(';')[0]!.trim();
  const buf = new Uint8Array(await res.arrayBuffer());
  const payload: Blob = { bytes: buf, mime };
  cache.set(cacheKey, payload);
  return payload;
}

async function fetchText(url: string): Promise<Text> {
  const cacheKey = `T::${url}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey)! as Text;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const mime = (res.headers.get('content-type') || guessMime(url)).split(';')[0]!.trim();
  const payload: Text = { text: await res.text(), mime };
  cache.set(cacheKey, payload);
  return payload;
}

function isExternal(url: string): boolean {
  return /^(https?:)?\/\//i.test(url);
}
function absolutize(url: string): string {
  return url.startsWith('//') ? 'https:' + url : url;
}
function guessMime(url: string, fallback = 'application/octet-stream'): string {
  try {
    const path = new URL(url, 'http://x').pathname;
    const idx = path.lastIndexOf('.');
    if (idx < 0) return fallback;
    return MIME_BY_EXT[path.slice(idx).toLowerCase()] ?? fallback;
  } catch {
    return fallback;
  }
}
function toDataUri(b: Blob): string {
  // Browser-safe base64 of binary bytes.
  let bin = '';
  for (let i = 0; i < b.bytes.length; i++) bin += String.fromCharCode(b.bytes[i]!);
  return `data:${b.mime};base64,${btoa(bin)}`;
}
function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

async function replaceAsync(
  input: string,
  regex: RegExp,
  asyncFn: (...m: string[]) => Promise<string>,
): Promise<string> {
  const matches: RegExpExecArray[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(regex.source, regex.flags);
  while ((m = re.exec(input)) !== null) {
    matches.push(m);
    if (m[0]!.length === 0) re.lastIndex++;
  }
  if (matches.length === 0) return input;
  const replacements = await Promise.all(matches.map((mm) => asyncFn(...mm)));
  // Walk + splice.
  let out = '';
  let lastIdx = 0;
  for (let i = 0; i < matches.length; i++) {
    const mm = matches[i]!;
    out += input.slice(lastIdx, mm.index) + replacements[i];
    lastIdx = mm.index + mm[0]!.length;
  }
  out += input.slice(lastIdx);
  return out;
}

async function inlineCssRefs(
  css: string,
  baseUrl: string | null,
  stats: InlinerStats,
): Promise<string> {
  // @import "url"; or @import url(...);
  css = await replaceAsync(
    css,
    /@import\s+(?:url\(\s*)?["']?([^"')]+)["']?\s*\)?\s*;?/gi,
    async (full, ref) => {
      try {
        const target = baseUrl ? new URL(ref, baseUrl).href : ref;
        if (!isExternal(target)) return full;
        const { text } = await fetchText(target);
        stats.inlinedCss++;
        const nested = await inlineCssRefs(text, target, stats);
        return `\n/* inlined @import */\n${nested}\n`;
      } catch (err) {
        stats.errors.push(`@import ${ref}: ${(err as Error).message}`);
        return full;
      }
    },
  );
  // url(...) — images, fonts
  css = await replaceAsync(css, /url\(\s*(["']?)([^"')]+)\1\s*\)/gi, async (full, _q, ref) => {
    if (ref.startsWith('data:') || ref.startsWith('#')) return full;
    try {
      const target = baseUrl ? new URL(ref, baseUrl).href : ref;
      if (!isExternal(target)) return full;
      const blob = await fetchBinary(target);
      stats.inlinedAssets++;
      return `url("${toDataUri(blob)}")`;
    } catch (err) {
      stats.errors.push(`url(${ref}): ${(err as Error).message}`);
      return full;
    }
  });
  return css;
}

async function inlineHtmlExternals(
  html: string,
  stats: InlinerStats,
  onProgress?: (msg: string) => void,
): Promise<string> {
  // <link rel="stylesheet" href="...">  → <style>...</style>; other <link> → stripped
  html = await replaceAsync(
    html,
    /<link\b([^>]*?)\bhref\s*=\s*["']([^"']+)["']([^>]*?)\/?>/gi,
    async (full, pre, href, post) => {
      const attrs = `${pre} ${post}`;
      const isStyle = /rel\s*=\s*["']?stylesheet["']?/i.test(attrs);
      const isIcon = /rel\s*=\s*["']?(icon|shortcut icon|apple-touch-icon)["']?/i.test(attrs);
      if (!isExternal(href)) return full;
      try {
        if (isStyle) {
          onProgress?.(`Inlining stylesheet: ${href}`);
          const { text } = await fetchText(href);
          const inlined = await inlineCssRefs(text, absolutize(href), stats);
          stats.inlinedCss++;
          return `<style>\n/* inlined external stylesheet */\n${inlined}\n</style>`;
        }
        if (isIcon) {
          const blob = await fetchBinary(href);
          stats.inlinedAssets++;
          return full.replace(href, toDataUri(blob));
        }
        // Silently drop preconnect, dns-prefetch, etc. — do NOT echo the
        // url into a comment (validator scans comments for href="https://).
        stats.strippedLinks++;
        return '';
      } catch (err) {
        stats.errors.push(`<link href="${href}">: ${(err as Error).message}`);
        return full;
      }
    },
  );

  // <script src="...">
  html = await replaceAsync(
    html,
    /<script\b([^>]*?)\bsrc\s*=\s*["']([^"']+)["']([^>]*?)>\s*<\/script>/gi,
    async (full, pre, src, post) => {
      if (!isExternal(src)) return full;
      try {
        onProgress?.(`Inlining script: ${src}`);
        const { text } = await fetchText(src);
        stats.inlinedJs++;
        const typeMatch = (pre + post).match(/\btype\s*=\s*["']([^"']+)["']/i);
        const typeAttr = typeMatch ? ` type="${typeMatch[1]}"` : '';
        return `<script${typeAttr}>\n/* inlined external script */\n${text}\n</script>`;
      } catch (err) {
        stats.errors.push(`<script src="${src}">: ${(err as Error).message}`);
        return full;
      }
    },
  );

  // <img src=...>, <source src=...>, video poster, srcset
  html = await replaceAsync(
    html,
    /<(img|source|audio|video|track)\b([^>]*?)\b(src|poster|srcset)\s*=\s*["']([^"']+)["']/gi,
    async (full, tag, pre, attr, value) => {
      if (attr.toLowerCase() === 'srcset') {
        const parts = value.split(',').map((s: string) => s.trim());
        const replaced: string[] = [];
        for (const part of parts) {
          const [url, descriptor] = part.split(/\s+/, 2);
          if (!url) continue;
          if (!isExternal(url)) {
            replaced.push(part);
            continue;
          }
          try {
            const blob = await fetchBinary(url);
            stats.inlinedAssets++;
            replaced.push(`${toDataUri(blob)}${descriptor ? ' ' + descriptor : ''}`);
          } catch (err) {
            stats.errors.push(`srcset ${url}: ${(err as Error).message}`);
            replaced.push(part);
          }
        }
        return `<${tag}${pre}srcset="${replaced.join(', ')}"`;
      }
      if (value.startsWith('data:') || value.startsWith('#')) return full;
      if (!isExternal(value)) return full;
      try {
        const blob = await fetchBinary(value);
        stats.inlinedAssets++;
        return `<${tag}${pre}${attr}="${toDataUri(blob)}"`;
      } catch (err) {
        stats.errors.push(`<${tag} ${attr}="${value}">: ${(err as Error).message}`);
        return full;
      }
    },
  );

  // Inline <style> blocks — fetch their url()/@import refs.
  html = await replaceAsync(
    html,
    /<style\b([^>]*)>([\s\S]*?)<\/style>/gi,
    async (_full, attrs, css) => {
      const inlined = await inlineCssRefs(css, null, stats);
      return `<style${attrs}>${inlined}</style>`;
    },
  );

  return html;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(2)}MB`;
}

export const VALIDATOR_LIMITS = {
  maxBytes: VALIDATOR_MAX_BYTES,
};
